import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SoupKitchenOutlinedIcon from '@mui/icons-material/SoupKitchenOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined'
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SaveIcon from '@mui/icons-material/Save'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'

import CateringBandEditor, { bandsToRows, parseBandRows } from '../components/Catering/CateringBandEditor'
import CateringAreaEditor, { areasFromZones, areasToZoneInput, areasSnapshot } from '../components/Catering/CateringAreaEditor'
import { CATERING_DELIVERY_MODES, noticeLine, cateringTitles, isCateringItem } from '../utils/catering'
import { cateringTheme, CT, focusRing } from './cateringTheme'

/**
 * A restaurant's Catering settings (backend migration 131): how far ahead
 * catering is ordered, when the kitchen gets it, who delivers it and — when
 * its own drivers can — what they charge. Which items are catering is set on
 * Menus (a category's "Catering category" switch); this page only lists them.
 * The catering service fee and commission are the super admin's, not shown here.
 */

const SETTINGS_FIELDS = 'restaurantId leadTimeMinutes prepTimeMinutes deliveryMode allowPickup minimumOrder printOnOrder maxGuests'
const BAND_FIELDS = '_id maxMiles fee sortOrder'
const ZONE_FIELDS = '_id name sortOrder polygon { coordinates }'

const GET_CATERING = gql`
  query RestaurantCateringSettings($restaurantId: ID!) {
    restaurantCatering(restaurantId: $restaurantId) { ${SETTINGS_FIELDS} }
    restaurantCateringBands(restaurantId: $restaurantId) { ${BAND_FIELDS} }
    restaurantCateringZones(restaurantId: $restaurantId) { ${ZONE_FIELDS} }
  }
`
const GET_RESTAURANT = gql`
  query CateringRestaurant($id: String) {
    restaurant(id: $id) {
      _id
      name
      selfDelivery
      hasCatering
      location { coordinates }
      categories { _id title isCatering }
    }
  }
`
// Item names and categories only, for the per-category counts.
const GET_ITEMS = gql`
  query CateringCategoryItems($restaurantId: ID!) {
    advancedRestaurantItems(restaurantId: $restaurantId) { _id categoryName }
  }
`
const SAVE_CATERING = gql`
  mutation SaveRestaurantCatering($restaurantId: ID!, $input: CateringSettingsInput!) {
    saveRestaurantCatering(restaurantId: $restaurantId, input: $input) { ${SETTINGS_FIELDS} }
  }
`
const SAVE_BANDS = gql`
  mutation SaveRestaurantCateringBands($restaurantId: ID!, $bands: [CateringBandInput!]!) {
    saveRestaurantCateringBands(restaurantId: $restaurantId, bands: $bands) { ${BAND_FIELDS} }
  }
`
const SAVE_ZONES = gql`
  mutation SaveRestaurantCateringZones($restaurantId: ID!, $zones: [CateringZoneInput!]!) {
    saveRestaurantCateringZones(restaurantId: $restaurantId, zones: $zones) { ${ZONE_FIELDS} }
  }
`

const NOTICE_PRESETS = [
  { label: 'No notice', minutes: 0 },
  { label: '4 hours', minutes: 240 },
  { label: '12 hours', minutes: 720 },
  { label: '24 hours', minutes: 1440 },
  { label: '48 hours', minutes: 2880 },
  { label: '3 days', minutes: 4320 }
]
const MAX_NOTICE = 43200 // 30 days, the server's limit

const MODE_ICONS = {
  REGULAR: RepeatRoundedIcon,
  RUSHMYFOOD: DeliveryDiningOutlinedIcon,
  SELF: DirectionsCarFilledOutlinedIcon,
  PICKUP_ONLY: StorefrontOutlinedIcon
}

// A custom notice as the user would say it: whole days when it divides, else hours.
const splitNotice = minutes =>
  minutes >= 1440 && minutes % 1440 === 0
    ? { value: String(minutes / 1440), unit: 'days' }
    : { value: String(Math.round((minutes / 60) * 100) / 100), unit: 'hours' }

const noticeMinutes = (value, unit) => {
  const n = parseFloat(value)
  if (!Number.isFinite(n) || n < 0) return NaN
  return Math.round(n * (unit === 'days' ? 1440 : 60))
}

// "11:00 AM" — minutes from midnight on a 12-hour clock.
const clockOf = minutes => {
  const m = ((minutes % 1440) + 1440) % 1440
  const h = Math.floor(m / 60)
  const mm = String(m % 60).padStart(2, '0')
  return `${h % 12 === 0 ? 12 : h % 12}:${mm} ${h < 12 ? 'AM' : 'PM'}`
}

const money = n => `$${Number(n).toFixed(2)}`

// The form as loaded from the server.
const formFrom = s => {
  const preset = NOTICE_PRESETS.some(p => p.minutes === s.leadTimeMinutes)
  const custom = splitNotice(s.leadTimeMinutes)
  return {
    noticeMode: preset ? 'preset' : 'custom',
    lead: s.leadTimeMinutes,
    customValue: custom.value,
    customUnit: custom.unit,
    prep: String(s.prepTimeMinutes),
    deliveryMode: s.deliveryMode,
    allowPickup: s.allowPickup,
    minimum: s.minimumOrder != null ? String(s.minimumOrder) : '',
    maxGuests: s.maxGuests != null ? String(s.maxGuests) : '',
    printOnOrder: s.printOnOrder !== false
  }
}

const effectiveLead = f => (f.noticeMode === 'custom' ? noticeMinutes(f.customValue, f.customUnit) : f.lead)

// What decides "unsaved": the values the server would store.
const snapshotOf = f => JSON.stringify({
  lead: effectiveLead(f),
  prep: f.prep.trim(),
  deliveryMode: f.deliveryMode,
  allowPickup: f.deliveryMode === 'PICKUP_ONLY' ? true : f.allowPickup,
  minimum: f.minimum.trim(),
  maxGuests: f.maxGuests.trim(),
  printOnOrder: f.printOnOrder
})

// ---- Presentation (the Delivery page's surfaces) ----
const PAGE = {
  bgcolor: CT.page, color: CT.text, fontFamily: CT.font, fontSize: 15, lineHeight: 1.45,
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }
}
const CONTENT = {
  maxWidth: 1040, mx: 'auto', boxSizing: 'content-box',
  px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }
}
const CARD = { bgcolor: CT.card, border: `1px solid ${CT.border}`, borderRadius: '16px', boxShadow: 'none', overflow: 'hidden' }
const CARD_PAD = { p: { xs: 2.5, sm: '28px 32px 32px' } }
const CARD_TITLE = { fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: CT.text }
const BODY = { fontSize: 15, lineHeight: 1.5, color: CT.body, '& strong': { fontWeight: 700, color: CT.text } }
const HELPER = { fontSize: 13, lineHeight: 1.45, color: CT.secondary, '& strong': { color: CT.text } }
const TILE = { display: 'grid', placeItems: 'center', flexShrink: 0 }
const SUB_LABEL = { fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: CT.label, mb: 1 }
const NOTE = {
  display: 'flex', alignItems: 'flex-start', gap: 1.25,
  bgcolor: CT.noteBg, border: `1px solid ${CT.noteBorder}`, borderRadius: '14px', p: '16px 18px',
  fontSize: 14, lineHeight: 1.55, color: CT.label, '& strong': { fontWeight: 700, color: CT.text }
}

function Section({ id, icon, title, intro, children }) {
  return (
    <Paper component="section" aria-labelledby={id} elevation={0} sx={{ ...CARD, ...CARD_PAD }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box aria-hidden sx={{ ...TILE, width: 44, height: 44, borderRadius: '12px', bgcolor: CT.tileBg, color: CT.icon, '& svg': { fontSize: 22 } }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, pt: '2px' }}>
          <Typography component="h2" id={id} sx={CARD_TITLE}>{title}</Typography>
          {intro && <Typography sx={{ ...BODY, mt: 0.5, maxWidth: 720 }}>{intro}</Typography>}
        </Box>
      </Box>
      {children}
    </Paper>
  )
}

// The line a customer reads on the storefront, in the catering accent.
function CustomerSees({ children }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 2.5,
      px: 1.5, py: 1, borderRadius: '10px', bgcolor: CT.accentSoft, border: `1px solid ${CT.accentBorder}`
    }}>
      <RoomServiceRoundedIcon aria-hidden sx={{ fontSize: 18, color: CT.accent }} />
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: CT.secondary }}>Customers see</Typography>
      <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, color: CT.accent }}>{children}</Typography>
    </Box>
  )
}

// One delivery option: a real radio (arrow keys work), drawn as a card.
function ModeCard({ mode, selected, onSelect }) {
  const Icon = MODE_ICONS[mode.value]
  return (
    <Box component="label" sx={{
      position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.75, cursor: 'pointer',
      borderRadius: '14px', boxSizing: 'border-box',
      // 2px border when selected, so 1px less padding keeps the size.
      p: selected ? '15px 47px 15px 15px' : '16px 48px 16px 16px',
      border: selected ? `2px solid ${CT.text}` : `1px solid ${CT.btnBorder}`,
      bgcolor: selected ? CT.noteBg : CT.card,
      transition: 'border-color 150ms ease, background-color 150ms ease',
      '&:hover': selected ? {} : { borderColor: CT.inputBorder },
      '&:has(input:focus-visible)': focusRing
    }}>
      <Box component="input" type="radio" name="cateringDeliveryMode" value={mode.value} checked={selected}
        onChange={() => onSelect(mode.value)}
        sx={{ position: 'absolute', opacity: 0, width: '1px', height: '1px', m: 0, pointerEvents: 'none' }} />
      <Box aria-hidden sx={{
        ...TILE, width: 40, height: 40, borderRadius: '10px',
        bgcolor: selected ? CT.text : CT.tileBg, color: selected ? '#FFFFFF' : CT.icon,
        transition: 'background-color 150ms ease, color 150ms ease'
      }}>
        <Icon sx={{ fontSize: 21 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 16, fontWeight: 700, lineHeight: 1.3, color: CT.text }}>{mode.vendor}</Typography>
        <Typography component="span" sx={{ display: 'block', mt: 0.5, fontSize: 14, lineHeight: 1.45, color: selected ? CT.body : CT.secondary }}>{mode.desc}</Typography>
      </Box>
      <Box component="span" aria-hidden sx={{
        position: 'absolute', top: selected ? 13 : 14, right: selected ? 13 : 14,
        width: 22, height: 22, boxSizing: 'border-box', borderRadius: '50%', display: 'grid', placeItems: 'center',
        ...(selected ? { bgcolor: CT.text, color: '#FFFFFF' } : { border: `1.5px solid ${CT.inputBorder}` })
      }}>
        {selected && <CheckRoundedIcon sx={{ fontSize: 15 }} />}
      </Box>
    </Box>
  )
}

export default function Catering() {
  const restaurantId = localStorage.getItem('restaurantId')

  const [form, setForm] = useState(null)
  const [savedForm, setSavedForm] = useState(null) // the form as last loaded or saved
  const [rows, setRows] = useState([])
  const [savedRows, setSavedRows] = useState('[]')
  // Where its own drivers deliver catering (migration 132).
  const [areas, setAreas] = useState([])
  const [savedAreas, setSavedAreas] = useState([])
  const [errors, setErrors] = useState({})
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' })

  const { data, loading, error: loadError } = useQuery(GET_CATERING, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })
  const { data: restaurantData } = useQuery(GET_RESTAURANT, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network'
  })
  const { data: itemsData } = useQuery(GET_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network'
  })

  useEffect(() => {
    if (!data?.restaurantCatering) return
    const f = formFrom(data.restaurantCatering)
    setForm(f)
    setSavedForm(f)
    const r = bandsToRows(data.restaurantCateringBands)
    setRows(r)
    setSavedRows(JSON.stringify(r))
    const a = areasFromZones(data.restaurantCateringZones)
    setAreas(a)
    setSavedAreas(a)
  }, [data])

  const [mutSave] = useMutation(SAVE_CATERING)
  const [mutBands] = useMutation(SAVE_BANDS)
  const [mutZones] = useMutation(SAVE_ZONES)
  const [saving, setSaving] = useState(false)

  const restaurant = restaurantData?.restaurant
  const selfDeliveryOn = restaurant?.selfDelivery === true
  const coords = restaurant?.location?.coordinates
  const mapCenter = coords && coords.length >= 2 && Number.isFinite(parseFloat(coords[1]))
    ? { lat: parseFloat(coords[1]), lng: parseFloat(coords[0]) }
    : null

  // Catering categories, with how many items each holds.
  const cateringCategories = useMemo(() => {
    const cats = (restaurant?.categories || []).filter(c => c.isCatering)
    const items = itemsData?.advancedRestaurantItems || []
    return cats
      .map(c => ({
        ...c,
        count: itemsData ? items.filter(i => isCateringItem(i, cateringTitles([c]))).length : null
      }))
      .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }))
  }, [restaurant, itemsData])

  if (!restaurantId) {
    return (
      <ThemeProvider theme={cateringTheme}>
        <Box sx={PAGE}>
          <Box sx={{ ...CONTENT, pb: 7 }}>
            <Paper elevation={0} sx={{ ...CARD, p: { xs: 3, sm: 5 }, textAlign: 'center', maxWidth: 560, mx: 'auto' }}>
              <Typography sx={{ ...CARD_TITLE, mb: 1 }}>No restaurant selected</Typography>
              <Typography sx={BODY}>Open a restaurant first, then come back to its Catering page.</Typography>
            </Paper>
          </Box>
        </Box>
      </ThemeProvider>
    )
  }

  const mode = form?.deliveryMode
  // The restaurant's own drivers can carry catering: always on "My own
  // drivers", and on "Same as my regular orders" while self delivery is on.
  const ownDriversDeliver = mode === 'SELF' || (mode === 'REGULAR' && selfDeliveryOn)
  const lead = form ? effectiveLead(form) : 0
  const prepNum = form ? parseInt(form.prep, 10) : NaN
  const minimumNum = form && form.minimum.trim() !== '' ? parseFloat(form.minimum) : null
  // The largest group it caters for (migration 136); blank = no limit.
  const maxGuestsNum = form && form.maxGuests.trim() !== '' ? Number(form.maxGuests) : null

  const formDirty = !!form && !!savedForm && snapshotOf(form) !== snapshotOf(savedForm)
  const rowsDirty = ownDriversDeliver && JSON.stringify(rows) !== savedRows
  const areasDirty = ownDriversDeliver && areasSnapshot(areas) !== areasSnapshot(savedAreas)
  const hasArea = areas.some(a => a._path && a._path.length >= 3)
  const hasTiers = (parseBandRows(rows).bands || []).length > 0
  const dirty = formDirty || rowsDirty || areasDirty

  const update = patch => setForm(f => ({ ...f, ...patch }))

  const pickPreset = minutes => {
    update({ noticeMode: 'preset', lead: minutes })
    setErrors(e => ({ ...e, lead: '' }))
  }
  const pickCustom = () => {
    // Start the custom box from whatever notice is set now.
    const now = splitNotice(Number.isFinite(lead) ? lead : 1440)
    update({ noticeMode: 'custom', customValue: now.value, customUnit: now.unit })
  }

  const setMode = value => {
    update(value === 'PICKUP_ONLY' ? { deliveryMode: value, allowPickup: true } : { deliveryMode: value })
  }

  const discard = () => {
    setForm(savedForm)
    setRows(JSON.parse(savedRows))
    setAreas(savedAreas)
    setErrors({})
  }

  const validate = () => {
    const e = {}
    if (!Number.isInteger(lead) || lead < 0) e.lead = 'Enter how far ahead customers must order.'
    else if (lead > MAX_NOTICE) e.lead = 'Advance notice can be up to 30 days.'
    if (!Number.isInteger(prepNum) || String(prepNum) !== form.prep.trim() || prepNum < 5 || prepNum > 1440) {
      e.prep = 'Enter whole minutes from 5 to 1,440 (24 hours).'
    }
    if (minimumNum != null && (!Number.isFinite(minimumNum) || minimumNum < 0 || minimumNum > 100000)) {
      e.minimum = 'Enter an amount from $0 to $100,000, or leave it blank.'
    }
    if (maxGuestsNum != null && (!Number.isInteger(maxGuestsNum) || maxGuestsNum < 1 || maxGuestsNum > 10000)) {
      e.maxGuests = 'Enter whole guests from 1 to 10,000, or leave it blank.'
    }
    if (rowsDirty) {
      const parsed = parseBandRows(rows)
      if (parsed.error) e.bands = parsed.error
    }
    if (areasDirty && areas.some(a => !a._path || a._path.length < 3)) {
      e.areas = 'Each catering area needs at least 3 points. Redraw it or delete it.'
    }
    // "My own drivers" needs a tier: it prices their catering deliveries.
    if (!e.bands && mode === 'SELF' && !(parseBandRows(rows).bands || []).length) {
      e.bands = hasArea
        ? 'Add at least one tier. It sets what a catering delivery in your area costs.'
        : 'Add at least one tier. Your farthest tier is how far your drivers deliver catering.'
    }
    setErrors(e)
    return Object.values(e).every(v => !v)
  }

  const handleSave = async () => {
    if (!validate()) {
      setSnack({ open: true, severity: 'error', msg: 'Check the highlighted fields.' })
      return
    }
    setSaving(true)
    try {
      if (formDirty) {
        const input = {
          leadTimeMinutes: lead,
          prepTimeMinutes: prepNum,
          deliveryMode: form.deliveryMode,
          allowPickup: form.deliveryMode === 'PICKUP_ONLY' ? true : form.allowPickup,
          minimumOrder: minimumNum,
          maxGuests: maxGuestsNum,
          printOnOrder: form.printOnOrder
        }
        const res = await mutSave({ variables: { restaurantId, input } })
        const f = formFrom(res.data.saveRestaurantCatering)
        setForm(f)
        setSavedForm(f)
      }
      if (rowsDirty) {
        const { bands } = parseBandRows(rows)
        const res = await mutBands({ variables: { restaurantId, bands } })
        const r = bandsToRows(res.data.saveRestaurantCateringBands)
        setRows(r)
        setSavedRows(JSON.stringify(r))
      }
      if (areasDirty) {
        const res = await mutZones({ variables: { restaurantId, zones: areasToZoneInput(areas) } })
        const a = areasFromZones(res.data.saveRestaurantCateringZones)
        setAreas(a)
        setSavedAreas(a)
      }
      setSnack({ open: true, severity: 'success', msg: 'Catering settings saved' })
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: err.message || 'Could not save catering settings' })
    } finally {
      setSaving(false)
    }
  }

  // "A catering order wanted at 12:00 PM reaches your kitchen at 11:00 AM."
  const kitchenExample = Number.isInteger(prepNum) && prepNum >= 5 && prepNum <= 1440
    ? `A catering order wanted at 12:00 PM reaches your kitchen at ${clockOf(720 - prepNum)}${prepNum > 720 ? ' the day before' : ''}.`
    : null
  const deliveryLine = mode === 'PICKUP_ONLY' ? 'Pickup only' : form?.allowPickup ? 'Delivery or pickup' : 'Delivery only'

  return (
    <ThemeProvider theme={cateringTheme}>
      <Box sx={PAGE}>
        <Box sx={{ ...CONTENT, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header */}
          <Box component="header" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box aria-hidden sx={{ ...TILE, width: 52, height: 52, borderRadius: '14px', bgcolor: CT.accentSoft, color: CT.accent, border: `1px solid ${CT.accentBorder}` }}>
              <RoomServiceRoundedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
              <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: CT.text }}>Catering</Typography>
              <Typography sx={{ fontSize: 15, color: CT.secondary, mt: 0.5 }}>
                How far ahead customers order catering, when your kitchen gets it, and how it reaches them
              </Typography>
            </Box>
            {restaurant && (
              <Box component="span" sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, height: 32, px: 1.5, borderRadius: '999px',
                fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                ...(restaurant.hasCatering
                  ? { bgcolor: CT.onBg, color: CT.onText, border: `1px solid ${CT.onBorder}` }
                  : { bgcolor: CT.card, color: CT.secondary, border: `1px solid ${CT.btnBorder}` })
              }}>
                <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: restaurant.hasCatering ? CT.onText : CT.inputBorder }} />
                {restaurant.hasCatering ? 'On your storefront' : 'Not on your storefront yet'}
              </Box>
            )}
          </Box>

          {loadError && <Alert severity="error">{loadError.message}</Alert>}
          {loading && !form && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
          )}

          {form && (
            <>
              {/* Advance notice */}
              <Section id="catering-notice" icon={<ScheduleRoundedIcon />} title="Advance notice"
                intro="How far ahead customers must place a catering order. Checkout only offers times at least this far away.">
                <Box role="group" aria-label="Advance notice" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {NOTICE_PRESETS.map(p => {
                    const on = form.noticeMode === 'preset' && form.lead === p.minutes
                    return (
                      <Chip key={p.minutes} label={p.label} size="small" clickable aria-pressed={on}
                        color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                        onClick={() => pickPreset(p.minutes)} />
                    )
                  })}
                  <Chip label="Custom" size="small" clickable aria-pressed={form.noticeMode === 'custom'}
                    color={form.noticeMode === 'custom' ? 'primary' : 'default'}
                    variant={form.noticeMode === 'custom' ? 'filled' : 'outlined'}
                    onClick={pickCustom} />
                </Box>

                {form.noticeMode === 'custom' && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mt: 2.5 }}>
                    <TextField label="Notice" type="number" size="small" value={form.customValue}
                      onChange={e => { update({ customValue: e.target.value }); setErrors(x => ({ ...x, lead: '' })) }}
                      inputProps={{ min: 0, step: form.customUnit === 'days' ? 1 : 0.5 }}
                      error={!!errors.lead} sx={{ width: 140 }} />
                    <TextField select label="Unit" size="small" value={form.customUnit}
                      onChange={e => update({ customUnit: e.target.value })} sx={{ width: 140 }}>
                      <MenuItem value="hours">hours</MenuItem>
                      <MenuItem value="days">days</MenuItem>
                    </TextField>
                  </Box>
                )}
                {errors.lead && <Typography role="alert" sx={{ ...HELPER, color: CT.danger, mt: 1 }}>{errors.lead}</Typography>}

                <CustomerSees>{Number.isFinite(lead) && lead > 0 ? noticeLine(lead) : 'No advance notice needed'}</CustomerSees>
              </Section>

              {/* Kitchen prep time */}
              <Section id="catering-prep" icon={<SoupKitchenOutlinedIcon />} title="Kitchen prep time"
                intro="How long before the wanted time a catering order reaches your kitchen and its ticket prints.">
                <TextField label="Minutes before the wanted time" type="number" size="small" value={form.prep}
                  onChange={e => { update({ prep: e.target.value }); setErrors(x => ({ ...x, prep: '' })) }}
                  inputProps={{ min: 5, max: 1440, step: 5 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                  error={!!errors.prep} helperText={errors.prep || kitchenExample || ' '}
                  sx={{ width: { xs: '100%', sm: 300 } }} />
                {Number.isInteger(prepNum) && Number.isInteger(lead) && prepNum > lead && !errors.prep && (
                  <Box sx={{ ...NOTE, mt: 2 }}>
                    <InfoOutlinedIcon aria-hidden sx={{ fontSize: 20, color: CT.noteIcon, mt: '1px' }} />
                    <span>
                      Your prep time is longer than your advance notice, so an order placed at the last minute reaches
                      your kitchen <strong>right away</strong>.
                    </span>
                  </Box>
                )}
                {/* One clickable row, like Allow pickup below. */}
                <FormControlLabel
                  labelPlacement="start"
                  control={<Switch checked={!!form.printOnOrder} onChange={e => update({ printOnOrder: e.target.checked })} />}
                  label={
                    <Box>
                      <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 700, color: CT.text }}>Also print when the order is placed</Typography>
                      <Typography component="span" sx={{ display: 'block', fontSize: 14, lineHeight: 1.45, color: CT.secondary }}>
                        Your tablet prints a ticket marked <strong>SCHEDULED</strong> with the date and time as soon as a catering order comes in, so you can plan and shop. The kitchen ticket still prints at the time above.
                      </Typography>
                    </Box>
                  }
                  sx={{
                    m: 0, mt: 2.5, width: '100%', boxSizing: 'border-box', justifyContent: 'space-between', gap: 2,
                    borderRadius: '12px', p: '14px 16px', cursor: 'pointer',
                    border: `1px solid ${CT.btnBorder}`, bgcolor: CT.card,
                    '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                    '& strong': { color: CT.text }
                  }}
                />
              </Section>

              {/* Catering delivery */}
              <Section id="catering-delivery" icon={<LocalShippingOutlinedIcon />} title="Catering delivery"
                intro="Who delivers catering orders. This can differ from your regular orders.">
                <Box role="radiogroup" aria-labelledby="catering-delivery" sx={{
                  display: 'grid', gap: '12px', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }
                }}>
                  {CATERING_DELIVERY_MODES.map(m => (
                    <ModeCard key={m.value} mode={m} selected={mode === m.value} onSelect={setMode} />
                  ))}
                </Box>

                {/* Catering by their own drivers stands alone: the catering tiers
                    set how far they deliver, so there is nothing to set up in
                    Self Delivery or Delivery Zones (those run regular orders). */}
                {mode === 'SELF' && (
                  <Box sx={{ ...NOTE, mt: 2 }}>
                    <InfoOutlinedIcon aria-hidden sx={{ fontSize: 20, color: CT.noteIcon, mt: '1px' }} />
                    <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                      Set where your drivers deliver catering in <strong>My catering delivery area</strong> below, and what it costs in <strong>My catering delivery fees</strong>.
                      Your regular orders, Self Delivery and Delivery Zones are not affected.
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'grid', gap: 2, mt: 3, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, alignItems: 'start' }}>
                  {/* One clickable row: the label is the FormControlLabel, so a click anywhere toggles. */}
                  <FormControlLabel
                    labelPlacement="start"
                    disabled={mode === 'PICKUP_ONLY'}
                    control={<Switch checked={mode === 'PICKUP_ONLY' ? true : form.allowPickup}
                      onChange={e => update({ allowPickup: e.target.checked })} />}
                    label={
                      <Box>
                        <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 700, color: CT.text }}>Allow pickup</Typography>
                        <Typography component="span" sx={{ display: 'block', fontSize: 14, lineHeight: 1.45, color: CT.secondary }}>
                          {mode === 'PICKUP_ONLY' ? 'Always on with Pickup only.' : 'Customers can also pick up their catering order.'}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      m: 0, width: '100%', boxSizing: 'border-box', justifyContent: 'space-between', gap: 2,
                      borderRadius: '12px', p: '14px 16px', cursor: mode === 'PICKUP_ONLY' ? 'not-allowed' : 'pointer',
                      border: `1px solid ${CT.btnBorder}`, bgcolor: CT.card,
                      '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                      '& .MuiFormControlLabel-label.Mui-disabled': { color: 'inherit' }
                    }}
                  />
                  <TextField label="Catering minimum (optional)" type="number" size="small" value={form.minimum}
                    onChange={e => { update({ minimum: e.target.value }); setErrors(x => ({ ...x, minimum: '' })) }}
                    placeholder="No minimum"
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    error={!!errors.minimum}
                    helperText={errors.minimum || 'Used instead of your regular minimum for catering carts.'}
                    fullWidth />
                  <TextField label="Largest group (optional)" type="number" size="small" value={form.maxGuests}
                    onChange={e => { update({ maxGuests: e.target.value.replace(/[^0-9]/g, '') }); setErrors(x => ({ ...x, maxGuests: '' })) }}
                    placeholder="No limit"
                    inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
                    InputProps={{ endAdornment: <InputAdornment position="end">guests</InputAdornment> }}
                    error={!!errors.maxGuests}
                    helperText={errors.maxGuests || 'Customers planning for more guests won’t see you on the Catering page.'}
                    fullWidth />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5 }}>
                  <CustomerSees>{deliveryLine}</CustomerSees>
                  {minimumNum != null && Number.isFinite(minimumNum) && minimumNum > 0 && (
                    <CustomerSees>Catering minimum {money(minimumNum)}</CustomerSees>
                  )}
                </Box>
              </Section>

              {/* Own-driver catering area */}
              {ownDriversDeliver && (
                <Section id="catering-area" icon={<MapOutlinedIcon />} title="My catering delivery area"
                  intro={<>Where <strong>your own drivers</strong> deliver catering. Draw it, or use a driving distance from your restaurant. It is separate from Self Delivery and Delivery Zones, which run your regular orders.</>}>
                  {mapCenter ? (
                    <CateringAreaEditor
                      areas={areas}
                      onChange={a => { setAreas(a); setErrors(x => ({ ...x, areas: '' })) }}
                      center={mapCenter}
                      restaurantName={restaurant?.name}
                      error={errors.areas}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  )}
                  <Typography sx={{ ...HELPER, mt: 2 }}>
                    {hasArea
                      ? (form.allowPickup
                          ? <>Customers outside your catering area can still order catering for <strong>pickup</strong>.</>
                          : <>Customers outside your catering area can&apos;t order catering from you.</>)
                      : mode === 'SELF' || hasTiers
                        ? <><strong>No area</strong> = your drivers deliver catering as far as your farthest tier.</>
                        : <><strong>No area</strong> = your regular delivery area is used.</>}
                  </Typography>
                </Section>
              )}

              {/* Own-driver catering tiers */}
              {ownDriversDeliver && (
                <Paper component="section" aria-label="My catering delivery fees" elevation={0} sx={CARD}>
                  <CateringBandEditor
                    rows={rows}
                    onChange={r => { setRows(r); setErrors(x => ({ ...x, bands: '' })) }}
                    error={errors.bands}
                    title="My catering delivery fees"
                    intro={<>What a catering delivery costs when <strong>your own drivers</strong> bring it, by driving distance from your restaurant.</>}
                    empty={mode === 'SELF'
                      ? <>Add a tier to set <strong>how far</strong> your drivers deliver catering and what it costs.</>
                      : <><strong>No catering tiers</strong> = your regular delivery fees are used{hasArea ? '' : ', in your regular delivery area'}.</>}
                    notes={mode === 'SELF'
                      ? [
                          hasArea
                            ? <>Inside your <strong>catering area</strong>, the first tier that reaches an address prices it. Your farthest tier covers the rest of the area.</>
                            : <>Your <strong>farthest tier</strong> is as far as your drivers deliver catering, until you draw a catering area.</>,
                          form.allowPickup
                            ? <>Customers farther away can still order catering for <strong>pickup</strong>.</>
                            : <>Customers farther away can&apos;t order catering from you.</>
                        ]
                      : [
                          <><strong>No catering tiers</strong> = your regular delivery fees are used.</>,
                          hasArea
                            ? <>Inside your <strong>catering area</strong>, the first tier that reaches an address prices it.</>
                            : <>With tiers, your <strong>farthest tier</strong> is as far as your drivers deliver catering, until you draw a catering area.</>,
                          <>These apply only when <strong>your drivers</strong> deliver a catering order. When RushMyFood delivers, RushMyFood&apos;s catering fees apply.</>
                        ]}
                  />
                </Paper>
              )}

              {/* Catering categories (read-only) */}
              <Section id="catering-categories" icon={<CategoryOutlinedIcon />} title="Catering categories"
                intro="Items in these categories make up your Catering section. Customers order them on their own, never in the same cart as regular items.">
                {cateringCategories.length === 0 ? (
                  <Box sx={{ border: `1px dashed ${CT.inputBorder}`, borderRadius: '14px', p: { xs: '20px', sm: '24px 28px' }, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5 }}>
                    <Box aria-hidden sx={{ ...TILE, width: 48, height: 48, borderRadius: '14px', bgcolor: CT.accentSoft, color: CT.accent, border: `1px solid ${CT.accentBorder}` }}>
                      <RoomServiceRoundedIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: CT.text }}>No catering categories yet</Typography>
                      <Typography sx={{ ...BODY, fontSize: 14, mt: 0.5 }}>
                        On <strong>Menus › Categories</strong>, edit a category (or create one) and turn on <strong>Catering category</strong>.
                        Its items become your Catering section.
                      </Typography>
                    </Box>
                    <Button component={RouterLink} to="/admin/advanced-menus" variant="outlined" endIcon={<ArrowForwardRoundedIcon />}>
                      Go to Menus
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${CT.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                      {cateringCategories.map(c => (
                        <Box component="li" key={c._id} sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5, px: 2, minHeight: 56,
                          '&:not(:last-of-type)': { borderBottom: `1px solid ${CT.rowBorder}` }
                        }}>
                          <RoomServiceRoundedIcon aria-hidden sx={{ fontSize: 20, color: CT.accent, flexShrink: 0 }} />
                          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: CT.text, overflowWrap: 'anywhere' }}>{c.title}</Typography>
                          {c.count != null && (
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.count ? CT.secondary : CT.danger, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {c.count === 0 ? 'No items yet' : `${c.count} item${c.count === 1 ? '' : 's'}`}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 2 }}>
                      <Typography sx={HELPER}>Add or change catering categories on <strong>Menus › Categories</strong>.</Typography>
                      <Button component={RouterLink} to="/admin/advanced-menus" size="small" variant="outlined" endIcon={<ArrowForwardRoundedIcon />}>
                        Go to Menus
                      </Button>
                    </Box>
                  </>
                )}
              </Section>

              {/* Save bar: stays at the bottom of the screen while scrolling */}
              <Box sx={{
                position: 'sticky', bottom: 0, zIndex: 3, mt: -1,
                mx: { xs: -2, sm: -3, md: -5 }, px: { xs: 2, sm: 3, md: 5 }, py: 1.5,
                bgcolor: 'rgba(244, 245, 247, 0.94)', backdropFilter: 'blur(6px)', borderTop: `1px solid ${CT.border}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                  <Typography role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, fontWeight: dirty ? 700 : 500, color: dirty ? CT.text : CT.secondary }}>
                    {dirty
                      ? <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: CT.amber }} />
                      : <CheckRoundedIcon aria-hidden sx={{ fontSize: 18, color: CT.secondary }} />}
                    {dirty ? 'Unsaved changes' : 'All changes saved'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.25 }}>
                    {dirty && <Button variant="outlined" onClick={discard} disabled={saving}>Discard</Button>}
                    <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  )
}
